/**
 * Score Overlay Configuration Component
 * 
 * Allows users to configure score overlay preset, position, colors, and display options.
 */

import React from 'react';
import { ScoreOverlayConfig as ScoreOverlayConfigType, defaultOverlayConfig } from '../../types/overlayConfig';

interface ScoreOverlayConfigProps {
  config: ScoreOverlayConfigType;
  onChange: (config: ScoreOverlayConfigType) => void;
}

const ScoreOverlayConfig: React.FC<ScoreOverlayConfigProps> = React.memo(({
  config,
  onChange,
}) => {
  // Temporarily disable gradient settings (visible but non-interactable)
  const isGradientDisabled = true;
  
  // Temporarily disable match time and auto-update settings (visible but non-interactable)
  const isMatchTimeSettingsDisabled = true;
  
  const presetOptions: Array<{ value: ScoreOverlayConfigType['preset']; label: string }> = [
    { value: 'preset1', label: 'Preset 1 (Simple)' },
    { value: 'preset2', label: 'Preset 2 (Big)' },
  ];

  const positionOptions: Array<{ value: ScoreOverlayConfigType['position']; label: string }> = [
    { value: 'top-left', label: 'Top Left' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'top', label: 'Top (Centered)' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom-right', label: 'Bottom Right' },
    { value: 'bottom', label: 'Bottom (Centered)' },
  ];

  const gradientTypeOptions: Array<{ value: ScoreOverlayConfigType['gradient']['type']; label: string }> = [
    { value: 'horizontal', label: 'Horizontal' },
    { value: 'vertical', label: 'Vertical' },
    { value: 'radial', label: 'Radial' },
  ];

  const defaultConfig = defaultOverlayConfig.scoreOverlay!;

  // Ensure disabled fields are set to false in config
  React.useEffect(() => {
    if (isMatchTimeSettingsDisabled) {
      const needsUpdate = config.showMatchTime !== false || config.updateOnGoals !== false;
      if (needsUpdate) {
        onChange({
          ...config,
          showMatchTime: false,
          updateOnGoals: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMatchTimeSettingsDisabled]); // Only run when disabled state changes

  const handleColorChange = (colorKey: 'primary' | 'secondary' | 'textColor', value: string) => {
    const newColors = {
      ...config.colors,
      [colorKey]: value,
    };
    
    // Update gradient colors if they match the changed color
    // Always provide a gradient object (use defaults if not present)
    const currentGradient = config.gradient || {
      enabled: false,
      type: 'horizontal' as const,
      direction: 0,
      startColor: config.colors.primary,
      endColor: config.colors.secondary,
      stops: [50, 50],
    };

    const gradient = {
      ...currentGradient,
      startColor: currentGradient.startColor === config.colors.primary && colorKey === 'primary' 
        ? value 
        : currentGradient.startColor === config.colors.secondary && colorKey === 'secondary'
        ? value
        : currentGradient.startColor,
      endColor: currentGradient.endColor === config.colors.primary && colorKey === 'primary'
        ? value
        : currentGradient.endColor === config.colors.secondary && colorKey === 'secondary'
        ? value
        : currentGradient.endColor,
    };

    onChange({
      ...config,
      colors: newColors,
      gradient,
    });
  };

  const handleReset = () => {
    onChange({
      ...config,
      colors: {
        primary: defaultConfig.colors.primary,
        secondary: defaultConfig.colors.secondary,
        textColor: defaultConfig.colors.textColor,
      },
      opacity: defaultConfig.opacity,
      gradient: {
        enabled: defaultConfig.gradient.enabled,
        type: defaultConfig.gradient.type,
        direction: defaultConfig.gradient.direction,
        startColor: defaultConfig.gradient.startColor,
        endColor: defaultConfig.gradient.endColor,
        stops: defaultConfig.gradient.stops,
      },
    });
  };

  return (
    <div className="space-y-3">
      {/* Preset Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Design Preset
        </label>
        <select
          value={config.preset}
          onChange={(e) => onChange({
            ...config,
            preset: e.target.value as ScoreOverlayConfigType['preset'],
          })}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {presetOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Position Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Position
        </label>
        <select
          value={config.position}
          onChange={(e) => onChange({
            ...config,
            position: e.target.value as ScoreOverlayConfigType['position'],
          })}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {positionOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Size Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Size
        </label>
        <select
          value={config.size || 'md'}
          onChange={(e) => onChange({
            ...config,
            size: e.target.value as ScoreOverlayConfigType['size'],
          })}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="xs">Extra Small (Mobile)</option>
          <option value="sm">Small (Mobile)</option>
          <option value="md">Medium (Desktop)</option>
          <option value="lg">Large (Desktop)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Smaller sizes are recommended for mobile screens (9:16, 1:1)
        </p>
      </div>

      {/* Color Configuration */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-700">Colors</label>
          <button
            onClick={handleReset}
            className="text-xs text-blue-600 hover:text-blue-800"
            type="button"
          >
            Reset
          </button>
        </div>
        
          <div className="space-y-2">
            {/* Primary Color */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Primary
                <span className="ml-1 text-gray-400" title="Main brand/accent color used for backgrounds and highlights">(?)</span>
              </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={config.colors.primary}
                onChange={(e) => handleColorChange('primary', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={config.colors.primary}
                onChange={(e) => handleColorChange('primary', e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="#0B66FF"
              />
            </div>
          </div>

          {/* Secondary Color */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Secondary
              <span className="ml-1 text-gray-400" title="Secondary accent color used for contrast and gradients">(?)</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={config.colors.secondary}
                onChange={(e) => handleColorChange('secondary', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={config.colors.secondary}
                onChange={(e) => handleColorChange('secondary', e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="#FFFFFF"
              />
            </div>
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Text Color
              <span className="ml-1 text-gray-400" title="Color of all text overlays (scores, match time, etc.)">(?)</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={config.colors.textColor}
                onChange={(e) => handleColorChange('textColor', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={config.colors.textColor}
                onChange={(e) => handleColorChange('textColor', e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="#111827"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Opacity Configuration */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <label className="block text-xs font-medium text-gray-700">Opacity</label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={config.opacity ?? 1.0}
            onChange={(e) => onChange({
              ...config,
              opacity: parseFloat(e.target.value),
            })}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <span className="text-xs text-gray-600 w-10 text-right">
            {((config.opacity ?? 1.0) * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Gradient Configuration */}
      <div className={`space-y-2 pt-2 border-t border-gray-200 ${isGradientDisabled ? 'opacity-60' : ''}`}>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="enable-gradient"
            checked={config.gradient?.enabled ?? false}
            disabled={isGradientDisabled}
            onChange={(e) => !isGradientDisabled && onChange({
              ...config,
              gradient: {
                ...config.gradient,
                enabled: e.target.checked,
                type: config.gradient?.type || 'horizontal',
                direction: config.gradient?.direction ?? 0,
                startColor: config.gradient?.startColor || config.colors.primary,
                endColor: config.gradient?.endColor || config.colors.secondary,
                stops: config.gradient?.stops || [50, 50],
              },
            })}
            className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <label htmlFor="enable-gradient" className={`text-xs font-medium ${isGradientDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}>
            Enable Gradient
          </label>
        </div>

        {config.gradient?.enabled && (
          <div className="space-y-2 pl-6">
            {/* Gradient Type */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isGradientDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                Gradient Type
              </label>
              <select
                value={config.gradient.type || 'horizontal'}
                disabled={isGradientDisabled}
                onChange={(e) => !isGradientDisabled && onChange({
                  ...config,
                  gradient: {
                    ...config.gradient!,
                    type: e.target.value as 'horizontal' | 'vertical' | 'radial',
                  },
                })}
                className={`w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
              >
                {gradientTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Direction (for horizontal/vertical) */}
            {config.gradient.type !== 'radial' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Direction (degrees)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="1"
                    value={config.gradient.direction ?? 0}
                    onChange={(e) => onChange({
                      ...config,
                      gradient: {
                        ...config.gradient!,
                        direction: parseInt(e.target.value),
                      },
                    })}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <span className="text-xs text-gray-600 w-12 text-right">
                    {config.gradient.direction ?? 0}°
                  </span>
                </div>
              </div>
            )}

            {/* Radial Center Position (for radial) */}
            {config.gradient.type === 'radial' && (
              <div className="space-y-2">
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${isGradientDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                    Center X (%)
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={config.gradient.stops?.[0] ?? 50}
                      disabled={isGradientDisabled}
                      onChange={(e) => !isGradientDisabled && onChange({
                        ...config,
                        gradient: {
                          ...config.gradient!,
                          stops: [parseInt(e.target.value), config.gradient.stops?.[1] ?? 50],
                        },
                      })}
                      className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-blue-600 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    />
                    <span className="text-xs text-gray-600 w-10 text-right">
                      {config.gradient.stops?.[0] ?? 50}%
                    </span>
                  </div>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${isGradientDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                    Center Y (%)
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={config.gradient.stops?.[1] ?? 50}
                      disabled={isGradientDisabled}
                      onChange={(e) => !isGradientDisabled && onChange({
                        ...config,
                        gradient: {
                          ...config.gradient!,
                          stops: [config.gradient.stops?.[0] ?? 50, parseInt(e.target.value)],
                        },
                      })}
                      className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-blue-600 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    />
                    <span className="text-xs text-gray-600 w-10 text-right">
                      {config.gradient.stops?.[1] ?? 50}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Start Color */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isGradientDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                Start Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={config.gradient.startColor || config.colors.primary}
                  disabled={isGradientDisabled}
                  onChange={(e) => !isGradientDisabled && onChange({
                    ...config,
                    gradient: {
                      ...config.gradient!,
                      startColor: e.target.value,
                    },
                  })}
                  className={`w-8 h-8 border border-gray-300 rounded ${isGradientDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                />
                <input
                  type="text"
                  value={config.gradient.startColor || config.colors.primary}
                  disabled={isGradientDisabled}
                  onChange={(e) => !isGradientDisabled && onChange({
                    ...config,
                    gradient: {
                      ...config.gradient!,
                      startColor: e.target.value,
                    },
                  })}
                  className={`flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  placeholder="#0B66FF"
                />
              </div>
            </div>

            {/* End Color */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isGradientDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                End Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={config.gradient.endColor || config.colors.secondary}
                  disabled={isGradientDisabled}
                  onChange={(e) => !isGradientDisabled && onChange({
                    ...config,
                    gradient: {
                      ...config.gradient!,
                      endColor: e.target.value,
                    },
                  })}
                  className={`w-8 h-8 border border-gray-300 rounded ${isGradientDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                />
                <input
                  type="text"
                  value={config.gradient.endColor || config.colors.secondary}
                  disabled={isGradientDisabled}
                  onChange={(e) => !isGradientDisabled && onChange({
                    ...config,
                    gradient: {
                      ...config.gradient!,
                      endColor: e.target.value,
                    },
                  })}
                  className={`flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  placeholder="#FFFFFF"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Display Options */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        {/* Show Tournament Logo */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="show-tournament-logo"
            checked={config.showTournamentLogo ?? false}
            onChange={(e) => onChange({
              ...config,
              showTournamentLogo: e.target.checked,
            })}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="show-tournament-logo" className="text-xs text-gray-700">
            Show tournament logo in center
          </label>
        </div>

        {/* Show Match Time */}
        <div className={`flex items-center space-x-2 ${isMatchTimeSettingsDisabled ? 'opacity-60' : ''}`}>
          <input
            type="checkbox"
            id="show-match-time"
            checked={isMatchTimeSettingsDisabled ? false : (config.showMatchTime ?? false)}
            disabled={isMatchTimeSettingsDisabled}
            onChange={(e) => !isMatchTimeSettingsDisabled && onChange({
              ...config,
              showMatchTime: e.target.checked,
            })}
            className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isMatchTimeSettingsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <label htmlFor="show-match-time" className={`text-xs ${isMatchTimeSettingsDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}>
            Show match time (e.g., "45'")
          </label>
        </div>

        {/* Update on Goals */}
        <div className={`flex items-center space-x-2 ${isMatchTimeSettingsDisabled ? 'opacity-60' : ''}`}>
          <input
            type="checkbox"
            id="update-on-goals"
            checked={isMatchTimeSettingsDisabled ? false : (config.updateOnGoals ?? false)}
            disabled={isMatchTimeSettingsDisabled}
            onChange={(e) => !isMatchTimeSettingsDisabled && onChange({
              ...config,
              updateOnGoals: e.target.checked,
            })}
            className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isMatchTimeSettingsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <label htmlFor="update-on-goals" className={`text-xs ${isMatchTimeSettingsDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}>
            Update score on goals automatically
          </label>
        </div>
      </div>
    </div>
  );
});

ScoreOverlayConfig.displayName = 'ScoreOverlayConfig';

export default ScoreOverlayConfig;
